/**
 * Accès minimal à IndexedDB pour le mode hors ligne (sans dépendance).
 * Trois magasins :
 * - `responses` : réponses GET mises en cache (clé = utilisateur + URL normalisée) ;
 * - `outbox`    : écritures (progression) en attente de synchronisation, rejouées dans l'ordre ;
 * - `downloads` : formations rendues disponibles hors ligne.
 */

const DB_NAME = 'lyrra-offline';
const DB_VERSION = 1;

export type StoreName = 'responses' | 'outbox' | 'downloads';

let dbPromise: Promise<IDBDatabase> | null = null;

export function isIndexedDbAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    return false;
  }
}

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains('responses')) db.createObjectStore('responses');
      if (!db.objectStoreNames.contains('outbox')) db.createObjectStore('outbox', { keyPath: 'id', autoIncrement: true });
      if (!db.objectStoreNames.contains('downloads')) db.createObjectStore('downloads');
    };
    request.onsuccess = () => {
      const db = request.result;
      // Une autre version de l'application ouvre une base plus récente : libérer la connexion.
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    request.onerror = () => {
      dbPromise = null;
      reject(request.error ?? new Error('IndexedDB indisponible'));
    };
  });
  return dbPromise;
}

function run<T>(store: StoreName, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(store, mode);
        const request = fn(tx.objectStore(store));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        tx.onabort = () => reject(tx.error ?? new Error('Transaction annulée'));
      }),
  );
}

export function idbGet<T>(store: StoreName, key: IDBValidKey): Promise<T | undefined> {
  return run<T | undefined>(store, 'readonly', (s) => s.get(key) as IDBRequest<T | undefined>);
}

export function idbPut<T>(store: StoreName, value: T, key?: IDBValidKey): Promise<IDBValidKey> {
  return run<IDBValidKey>(store, 'readwrite', (s) => (key === undefined ? s.put(value) : s.put(value, key)));
}

export function idbDelete(store: StoreName, key: IDBValidKey): Promise<undefined> {
  return run<undefined>(store, 'readwrite', (s) => s.delete(key) as IDBRequest<undefined>);
}

export function idbGetAll<T>(store: StoreName): Promise<T[]> {
  return run<T[]>(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>);
}

export function idbGetAllKeys(store: StoreName): Promise<IDBValidKey[]> {
  return run<IDBValidKey[]>(store, 'readonly', (s) => s.getAllKeys());
}

export function idbCount(store: StoreName): Promise<number> {
  return run<number>(store, 'readonly', (s) => s.count());
}
